/**
 * The Compass panel (Sprint 103.1, mockup 3), opened from the compass chip. It
 * replaces tap-the-sun: the heading is corrected to true north on its own, so
 * all that is left to say is how good the compass is and how to wake it up.
 */

import type { ReactNode } from 'react';
import { degrees } from '../model/format.ts';
import { shownError, type CompassQuality } from '../model/heading.ts';
import { Icon, useWords } from './common.tsx';

export function CompassSheet({
  quality,
  errorDegrees,
  declinationDegrees,
  onClose
}: {
  readonly quality: CompassQuality;
  readonly errorDegrees: number | undefined;
  readonly declinationDegrees: number;
  readonly onClose: () => void;
}): ReactNode {
  const { t, language } = useWords();
  const rounded = Math.round(declinationDegrees * 10) / 10;
  const declination = `${rounded > 0 ? '+' : ''}${degrees(rounded, language, 1)}`;
  const badge =
    quality === 'unknown' || errorDegrees === undefined
      ? t('compass.settling')
      : quality === 'poor'
        ? t('compass.poor')
        : t(quality === 'good' ? 'compass.good' : 'compass.fair', {
            degrees: shownError(errorDegrees)
          });

  return (
    <section className="sheet" role="dialog" aria-labelledby="compass-title">
      <div className="sheet__handle" />
      <div className="row">
        <h1 id="compass-title" style={{ margin: 0, fontSize: '1.0625rem' }}>
          {t('compass.title')}
        </h1>
        <div className="spacer" />
        <button type="button" className="btn btn--primary" onClick={onClose}>
          {t('common.done')}
        </button>
      </div>
      <div className="compass-row">
        <Icon name="compass" />
        <div className="compass-row__body">
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <strong>{t('compass.accuracy')}</strong>
            <span
              className={
                quality === 'good'
                  ? 'badge badge--good'
                  : quality === 'poor'
                    ? 'badge badge--poor'
                    : 'badge'
              }
              role="status"
            >
              {badge}
            </span>
          </div>
          <span>{t('compass.trueNorth', { degrees: declination })}</span>
        </div>
      </div>
      <div className="compass-row">
        <Icon name="eight" />
        <div className="compass-row__body">
          <strong>{t('compass.eightTitle')}</strong>
          <span>{t('compass.eightBody')}</span>
        </div>
      </div>
    </section>
  );
}
